import api, { convertToISODateTime, handleApiError, fetchAllDataRest } from './client';

export const medicalCertificateService = {
  createCertificate: async (formData) => {
    try {
      const payload = {
        userId: formData.user,
        certificateNumber: formData.certificateNumber || null,
        issueDate: convertToISODateTime(formData.issueDate),
        expiryDate: convertToISODateTime(formData.expiryDate),
        certificateType: formData.certificateType,
        notes: formData.notes || null,
      };

      const response = await api.post('/api/medical-certificates', payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating certificate:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Errore nella creazione del certificato',
      };
    }
  },

  updateCertificate: async (id, certificateData) => {
    try {
      const payload = {
        certificateNumber: certificateData.certificateNumber || null,
        issueDate: convertToISODateTime(certificateData.issueDate),
        expiryDate: convertToISODateTime(certificateData.expiryDate),
        certificateType: certificateData.certificateType,
        notes: certificateData.notes || null,
      };

      const response = await api.put(`/api/medical-certificates/${id}`, payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating certificate:', error);
      return {
        success: false,
        error: error.response?.data?.message || "Errore nell'aggiornamento del certificato",
      };
    }
  },

  deleteCertificate: async (id) => {
    try {
      const response = await api.delete(`/api/medical-certificates/${id}`);
      return {
        success: response.data?.success ?? true,
      };
    } catch (error) {
      console.error('Error deleting certificate:', error);
      return {
        success: false,
        error: error.response?.data?.message || "Errore nell'eliminazione del certificato",
      };
    }
  },

  deleteFileCertificate: async (id) => {
    try {
      const response = await api.delete(`/api/medical-certificates/${id}/file`);
      return {
        success: response.data?.success ?? true,
      };
    } catch (error) {
      console.error('Error deleting certificate file:', error);
      return {
        success: false,
        error: error.response?.data?.message || "Errore nell'eliminazione del certificato",
      };
    }
  },

  archiveCertificate: async (id, isArchived) => {
    try {
      const response = await api.post(`/api/medical-certificates/${id}/archive`, { isArchived });
      return {
        success: true,
        data: response.data?.success ?? true,
        message: isArchived ? 'Certificato medico archiviato con successo' : 'Certificato medico ripristinato con successo',
      };
    } catch (error) {
      console.error('Archive medical certificate error:', error);
      return handleApiError(error, "Errore nell'archiviazione del certificato medico");
    }
  },

  getAllCertificates: async (statusFilter = null, page = 1, limit = 10, exportAll = false, archiveFilter = 'active') => {
    try {
      const params = {
        statusFilter: statusFilter === 'all' ? undefined : statusFilter,
        archiveFilter: archiveFilter || undefined,
        page,
        limit,
      };

      if (exportAll) {
        const result = await fetchAllDataRest('/api/medical-certificates', params, 'certificati medici');
        if (result.success) {
          const items = result.data?.items || result.data || [];
          return {
            success: true,
            data: items,
            pagination: {
              totalCount: result.data?.totalCount ?? items.length,
              totalPages: 1,
              currentPage: 1,
            },
          };
        }
        return { success: false, error: result.message };
      }

      const response = await api.get('/api/medical-certificates', { params });
      const data = response.data;
      return {
        success: true,
        data: data.items || [],
        pagination: {
          totalCount: data.totalCount || 0,
          totalPages: data.totalPages || 1,
          currentPage: data.currentPage || 1,
        },
      };
    } catch (error) {
      console.error('Error fetching all certificates:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Errore nel caricamento di tutti i certificati',
      };
    }
  },

  getUserCertificates: async (userId, statusFilter = null, page = 1, limit = 5, exportAll = false, archiveFilter = 'active') => {
    try {
      const params = {
        statusFilter: statusFilter === 'all' ? undefined : statusFilter,
        archiveFilter: archiveFilter || undefined,
        page,
        limit,
      };

      const url = `/api/medical-certificates/user/${userId}`;

      if (exportAll) {
        const result = await fetchAllDataRest(url, params, 'certificati medici utente');
        if (result.success) {
          const items = result.data?.items || result.data || [];
          return {
            success: true,
            data: items,
            pagination: {
              totalCount: result.data?.totalCount ?? items.length,
              totalPages: 1,
              currentPage: 1,
            },
          };
        }
        return { success: false, error: result.message };
      }

      const response = await api.get(url, { params });
      const data = response.data;
      return {
        success: true,
        data: data.items || [],
        pagination: {
          totalCount: data.totalCount || 0,
          totalPages: data.totalPages || 1,
          currentPage: data.currentPage || 1,
        },
      };
    } catch (error) {
      console.error('Error fetching user certificates:', error);
      return {
        success: false,
        error: error.response?.data?.message || "Errore nel caricamento dei certificati dell'utente",
      };
    }
  },

  downloadCertificate: async (id) => {
    try {
      const response = await api.get(`/api/File/certificate/${id}/download`, {
        responseType: 'blob',
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Download certificate error:', error);
      return {
        success: false,
        message: 'Errore nel download del certificato',
      };
    }
  },

  downloadCertificateFile: async (certificate) => {
    try {
      const response = await api.get(`/api/File/certificate/${certificate.id}/download`, {
        responseType: 'blob',
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = certificate.fileName || 'certificato';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
      }
      return { success: false, message: 'Errore durante il download del file' };
    } catch (error) {
      console.error('Download certificate file error:', error);
      return {
        success: false,
        message: 'Errore di connessione durante il download',
      };
    }
  },
};

export default medicalCertificateService;
